/**
 * Página de Agenda (Médico)
 * Visualiza as consultas agendadas em um calendário interativo
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/auth';
import { Sidebar } from '@/components/common/Sidebar';
import { DoctorAvailabilitySettings } from '@/components/dashboard';
import { getDoctorByUserId } from '@/services/doctors';
import type { DoctorDTO } from '@/services/doctors';
import { resolveAgendaTab, type AgendaTab } from '@/lib/agendaTabs';
import { 
  AlertCircle,
  Calendar, 
  Clock, 
  Loader2, 
  ChevronLeft,
  ChevronRight,
  MapPin,
  Sliders,
  Stethoscope,
  Video,
  Home,
  Info
} from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { getMyDoctorAppointments } from '@/services/appointments';
import type { AppointmentDTO } from '@/services/appointments';
import { appointmentsMatchCalendarDay, formatLocalDateKey } from '@/lib/calendarDate';
import { formatAppointmentReasonForDisplay } from '@/lib/appointmentReason';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const WEEKDAYS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

export default function AgendaPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isLoading, logout } = useAuth();
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [appointments, setAppointments] = useState<AppointmentDTO[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [doctorProfile, setDoctorProfile] = useState<DoctorDTO | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const profileFetchedRef = useRef(false);

  const activeTab = resolveAgendaTab(searchParams.get('tab'));

  const selectTab = (tab: AgendaTab) => {
    if (tab === 'consultas') {
      setSearchParams({}, { replace: true });
      return;
    }
    setSearchParams({ tab: 'configurar' }, { replace: true });
  };

  useEffect(() => {
    if (!isLoading && (!user || user.role !== UserRole.DOCTOR)) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    if (!user || user.role !== UserRole.DOCTOR) return;

    const loadAppointments = async () => {
      try {
        setIsLoadingData(true);

        // Intervalo do mês no horário local — toISOString transmite o instante correto ao backend
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1, 0, 0, 0, 0);
        const endOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0,
          23,
          59,
          59,
          999,
        );

        // Usa o médico autenticado (users.id) — mesmo vínculo das consultas persistidas
        const appointmentsResponse = await getMyDoctorAppointments(
          startOfMonth.toISOString(),
          endOfMonth.toISOString(),
        );

        if (appointmentsResponse.success === false) {
          throw new Error(appointmentsResponse.error || 'Falha ao carregar agenda');
        }

        setAppointments(appointmentsResponse.data || []);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Erro ao carregar sua agenda');
        setAppointments([]);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadAppointments();
  }, [user, currentDate]);

  useEffect(() => {
    if (activeTab !== 'configurar' || !user?.id || user.role !== UserRole.DOCTOR) return;
    if (profileFetchedRef.current) return;
    profileFetchedRef.current = true;

    const loadProfile = async () => {
      setIsLoadingProfile(true);
      setProfileError(null);
      try {
        const doctorResponse = await getDoctorByUserId(user.id);
        if (doctorResponse.success && doctorResponse.data) {
          setDoctorProfile(doctorResponse.data);
        } else {
          setProfileError(doctorResponse.error || 'Não foi possível carregar os horários da agenda.');
        }
      } catch (error) {
        console.error('Error loading doctor schedule profile:', error);
        setProfileError('Não foi possível carregar os horários da agenda.');
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  }, [activeTab, user]);

  // Calcula os dias do mês
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    setSelectedDate(null);
  };

  const getAppointmentsForDate = (date: Date) => {
    // Compara YYYY-MM-DD sem `new Date('YYYY-MM-DD')` (UTC) — evita D−1 no Brasil
    return appointmentsMatchCalendarDay(appointments, date);
  };

  const selectedDateAppointments = selectedDate ? getAppointmentsForDate(selectedDate) : [];

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = Array.from({ length: daysInMonth }, (_, i) => new Date(currentDate.getFullYear(), currentDate.getMonth(), i + 1));

  const handleDateClick = (date: Date) => {
    const dayAppointments = getAppointmentsForDate(date);
    if (dayAppointments.length > 0) {
      setSelectedDate(date);
      setIsModalOpen(true);
    } else {
      toast.info('Nenhuma consulta agendada para este dia');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={async () => {
          await logout();
          navigate('/login');
        }}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Minha Agenda</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {activeTab === 'configurar'
                  ? 'Defina horários disponíveis e antecedência mínima de agendamento'
                  : 'Visualize suas consultas agendadas'}
              </p>
            </div>
          </div>

          <div className="flex gap-1 mt-6 border-b border-gray-200 dark:border-neutral-800 -mb-6">
            <button
              type="button"
              onClick={() => selectTab('consultas')}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'consultas'
                  ? 'border-teal-600 text-teal-600 dark:text-teal-400 dark:border-teal-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Consultas
            </button>
            <button
              type="button"
              onClick={() => selectTab('configurar')}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'configurar'
                  ? 'border-teal-600 text-teal-600 dark:text-teal-400 dark:border-teal-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Sliders className="w-4 h-4" />
              Configurar agenda
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto p-8">
          {activeTab === 'configurar' ? (
            <div className="max-w-4xl">
              {isLoadingProfile ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-teal-600 animate-spin" aria-label="Carregando horários" />
                </div>
              ) : profileError ? (
                <div className="bg-amber-50 p-4 rounded-lg flex gap-3 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  <p>{profileError}</p>
                </div>
              ) : doctorProfile ? (
                <DoctorAvailabilitySettings
                  doctor={doctorProfile}
                  onUpdate={(updated) => setDoctorProfile(updated)}
                />
              ) : null}
            </div>
          ) : (
          <div className="max-w-5xl mx-auto">
            {/* Calendário */}
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-8 shadow-sm">
              {/* Cabeçalho do calendário */}
              <div className="flex items-center justify-between mb-8">
                <button 
                  onClick={handlePreviousMonth}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
                <div className="text-center">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {MONTHS[currentDate.getMonth()]}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{currentDate.getFullYear()}</p>
                </div>
                <button 
                  onClick={handleNextMonth}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              {/* Dias da semana */}
              {isLoadingData ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-teal-600 animate-spin" aria-label="Carregando consultas" />
                </div>
              ) : (
              <>
              <div className="grid grid-cols-7 gap-2 mb-2">
                {WEEKDAYS.map(day => (
                  <div key={day} className="text-center py-3 px-2">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{day}</p>
                  </div>
                ))}
              </div>

              {/* Dias do mês */}
              <div className="grid grid-cols-7 gap-4">
                {/* Espaços vazios antes do primeiro dia */}
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}

                {/* Dias do mês */}
                {days.map(date => {
                  const dayAppointments = getAppointmentsForDate(date);
                  const isToday = new Date().toDateString() === date.toDateString();
                  
                  return (
                    <button
                      key={formatLocalDateKey(date)}
                      onClick={() => handleDateClick(date)}
                      className={`aspect-square p-2 rounded-xl border-2 transition-all flex flex-col items-center justify-center cursor-pointer relative group ${
                        dayAppointments.length > 0
                          ? 'bg-white dark:bg-neutral-800 border-teal-500/20 hover:border-teal-500 shadow-sm' 
                          : 'bg-gray-50/50 dark:bg-neutral-900/50 border-transparent hover:border-gray-200 dark:hover:border-neutral-800'
                      } ${isToday ? 'ring-2 ring-teal-500 ring-offset-2 dark:ring-offset-neutral-950' : ''}`}
                    >
                      <span className={`text-lg font-bold ${
                        dayAppointments.length > 0
                          ? 'text-gray-900 dark:text-white' 
                          : 'text-gray-400 dark:text-gray-600'
                      }`}>
                        {date.getDate()}
                      </span>
                      
                      {dayAppointments.length > 0 && (
                        <div className="mt-1">
                          <Badge variant="secondary" className="bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400 border-none font-bold text-[10px] px-1.5 h-5">
                            {dayAppointments.length} {dayAppointments.length === 1 ? 'consulta' : 'consultas'}
                          </Badge>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              </>
              )}
            </div>
          </div>
          )}
        </main>
      </div>

      {/* Modal de Detalhes do Dia */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-800">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
              Consultas do Dia
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              {selectedDate?.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] p-6">
            <div className="space-y-4">
              {selectedDateAppointments
                .sort((a, b) => a.time.localeCompare(b.time))
                .map(apt => (
                <div
                  key={apt.id}
                  className="p-4 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800 hover:border-teal-500/50 transition-colors group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-teal-600" />
                      </div>
                      <span className="font-bold text-gray-900 dark:text-white text-lg">{apt.time}</span>
                    </div>
                    <Badge className={`font-medium ${
                      apt.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-none' :
                      apt.status === 'scheduled' ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 border-none' :
                      apt.status === 'cancelled' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-none' :
                      'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-none'
                    }`}>
                      {apt.status === 'completed' ? 'Concluída' :
                       apt.status === 'scheduled' ? 'Agendada' :
                       apt.status === 'cancelled' ? 'Cancelada' :
                       apt.status === 'reschedule_requested' ? 'Reagendamento' :
                       'Pendente'}
                    </Badge>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-neutral-700 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold text-xs">
                        {apt.patient?.user?.name?.charAt(0) || 'P'}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {apt.patient?.user?.name || 'Paciente'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Paciente</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                        {apt.type === 'video' ? (
                          <>
                            <Video className="w-3.5 h-3.5 text-teal-500" />
                            <span>Telemedicina</span>
                          </>
                        ) : apt.type === 'home' ? (
                          <>
                            <Home className="w-3.5 h-3.5 text-teal-500" />
                            <span>Domiciliar</span>
                          </>
                        ) : (
                          <>
                            <Stethoscope className="w-3.5 h-3.5 text-teal-500" />
                            <span>Consultório</span>
                          </>
                        )}
                      </div>
                      {apt.reason && (
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <Info className="w-3.5 h-3.5 text-teal-500" />
                          <span className="truncate">
                            {formatAppointmentReasonForDisplay(apt.reason)}
                          </span>
                        </div>
                      )}
                    </div>

                    {apt.address && apt.type === 'home' && (
                      <div className="pt-2 border-t border-gray-100 dark:border-neutral-800 flex items-start gap-2">
                        <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5" />
                        <span className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                          {apt.address.street}, {apt.address.number}<br />
                          {apt.address.neighborhood} - {apt.address.city}, {apt.address.state}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-neutral-800">
                    <button 
                      onClick={() => navigate(`/chat/${apt.id}`)}
                      className="w-full py-2.5 bg-teal-600 text-white text-xs font-bold rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 active:bg-teal-800 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Info className="w-3.5 h-3.5" />
                      Ver mais detalhes
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
